import { uiText } from "@/lib/i18n/uiText";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface DataPaginationProps {
  page: number; // 0-indexed
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  isLoading?: boolean;
}

/**
 * Reusable Prev/Next pagination control with page-size selector for large
 * data tables and lists backed by server-side `.range()` queries.
 */
export function DataPagination({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  isLoading,
}: DataPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = totalCount === 0 ? 0 : page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, totalCount);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-2" data-testid="data-pagination">
      <p className="text-sm text-muted-foreground" data-testid="pagination-range">
        {totalCount === 0 ? uiText("No results") : uiText("Showing {{v0}}–{{v1}} of {{v2}}", { v0: from, v1: to, v2: totalCount })}
      </p>
      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className="h-8 w-[110px]" data-testid="pagination-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((opt) => (
                <SelectItem key={opt} value={String(opt)}>
                  {opt}  {uiText("/ page")} </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          data-testid="pagination-prev"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 0 || isLoading}
        >
          <ChevronLeft className="h-4 w-4" />
           {uiText("Prev")} </Button>
        <span className="text-sm tabular-nums px-1" data-testid="pagination-indicator">
          {page + 1} / {totalPages}
        </span>
        <Button
          data-testid="pagination-next"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1 || isLoading}
        >
           {uiText("Next")} <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
